import { useState, useEffect, useMemo, useCallback } from 'react';
import { PlusOutlined, DashOutlined } from '@ant-design/icons';
import { Modal, Upload, Input } from 'antd';
import { useIntl } from 'react-intl';
import { useCreateFile, useGetAllfiles, useDeleteFile } from 'api/fileApi';
import _, { isArray } from 'lodash';
import styles from './MultipleUpload.module.css';
import '../../../styles/mediaLibraryOverride.css';
import { Notification } from 'components/Notification';
import { ReactMediaLibrary } from '../../../lib/react-media-library-patched';

const EMPTY_IDS = [];

const getIdsImage = (items, Arr) => {
  if (isArray(Arr)) _.map(items, (item) => {
    if (item?.id) {
      Arr.push(+item?.id);
    }
    if (item?.uid) {
      Arr.push(+item?.uid);
    }
  });
  return Arr;
};

const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const serializeFileList = (files) => {
  const mappedFiles = _.map(files, (file) => file?.response ?? null) ?? [];

  return _.filter(mappedFiles, (file) => !!file?.id);
};

const normalizeFileList = (files) =>
  _.map(files, (file) => ({
    url: file?.path,
    name: file?.name,
    uid: file?.id ? file.id.toString() : new Date().getTime(),
    response: file,
    status: 'done',
  })) ?? [];

export default function MultipleUpload({ value, onChange, fileMax = 1, typeMenu = false, multil = false }) {
  const intl = useIntl();
  const createMutation = useCreateFile();
  const deleteMutation = useDeleteFile();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [fileList, setFileList] = useState(() => normalizeFileList(value));
  const [isOpenLibImg, setIsOpenLibImg] = useState(false);
  const [defaultIdImg, setDefaultIdImg] = useState(null);
  const [searchValue, setSearchValue] = useState('');

  const { data, refetch } = useGetAllfiles();

  const fileLibraryList = useMemo(() => {
    const rawList = data?.data;
    if (!Array.isArray(rawList) || rawList.length === 0) return [];
    const result = new Array(rawList.length);
    for (let i = 0; i < rawList.length; i += 1) {
      const item = rawList[i];
      result[i] = {
        ...item,
        _id: item.id,
        thumbnailUrl: item.path,
        _sortTime: item?.created_at ? Date.parse(item.created_at) || 0 : 0,
        _sortId: Number(item?.id) || 0,
      };
    }
    result.sort((a, b) => {
      if (a._sortTime !== b._sortTime) return b._sortTime - a._sortTime;
      return b._sortId - a._sortId;
    });
    return result;
  }, [data?.data]);

  const searchResults = useMemo(() => {
    const keyword = searchValue.trim().toLowerCase();
    if (!keyword) return fileLibraryList;
    return fileLibraryList.filter((item) =>
      (item?.type || '').toLowerCase().includes(keyword),
    );
  }, [fileLibraryList, searchValue]);

  useEffect(() => {
    onChange?.(serializeFileList(fileList));
  }, [fileList]);

  useEffect(() => {
    if (fileList.length > 0) {
      setDefaultIdImg(getIdsImage(fileList, []));
    }
  }, []);

  const handleCancel = () => setPreviewOpen(false);
  const handleCancelLibImg = useCallback(() => {
    setSearchValue('');
    setIsOpenLibImg(false);
  }, []);

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
    setPreviewTitle(
      file.name || file.url.substring(file.url.lastIndexOf('/') + 1),
    );
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div
        style={{
          marginTop: 8,
        }}
      >
        Upload
      </div>
    </div>
  );

  const handleUploadImage = async (options) => {
    const data = { file: options };
    const { file } = data;

    const formData = new FormData();
    formData.append('file', file);

    if (typeMenu === 'menu') {
      formData.append('type', 'menu');
    } else {
      formData.append('type', 'image');
    }

    const result = await createMutation.mutateAsync(
      { data: formData },
      {
        onSuccess(response) {
          setFileList(normalizeFileList([response?.data]));
          refetch();
          Notification(
            'success',
            intl.formatMessage({
              id: `message.createSuccess`,
            }),
          );
        },
        onError(error) {
          Notification(
            'error',
            error.message ?? intl.formatMessage({ id: 'message.commonError' }),
          );
        },
      },
    );

    if (result?.data) {
      return true;
    }

    return false;
  };

  const handleSelectImage = async (value) => {
    const result = value;

    setDefaultIdImg(getIdsImage(value, []));
    if (Array(value) && value.length > 0) {
      setFileList(normalizeFileList(result));
      setIsOpenLibImg(false);
    }
  };

  const handleDeleteImage = async (items) => {
    const ids = getIdsImage(items, []);
    if (ids.length > 0) {
      deleteMutation.mutateAsync(ids, {
        onSuccess() {
          refetch();
          Notification(
            'success',
            intl.formatMessage({ id: 'message.deleteSuccess' }),
          );
        },
        onError(error) {
          Notification(
            'error',
            error.message ?? intl.formatMessage({ id: 'message.commonError' }),
          );
        },
      });
    }
  };

  const handleChangeSelect = useCallback((e) => {
    setSearchValue(e.target.value);
  }, []);

  const selectInputMediaLibrary = useCallback(() => {
    return (
      <Input
        style={{
          width: '95%',
          margin: '10px auto',
          display: 'flex',
          justifyContent: 'center',
          borderRadius: 8,
        }}
        placeholder="Search type image: image or audio or menu."
        onChange={handleChangeSelect}
      />
    );
  }, [handleChangeSelect]);

  return (
    <>
      {isOpenLibImg && (
        <ReactMediaLibrary
          defaultSelectedItemIds={defaultIdImg || EMPTY_IDS}
          topBarComponent={selectInputMediaLibrary}
          multiSelect={multil ?? false}
          fileLibraryList={searchResults}
          isOpen={isOpenLibImg}
          filesDeleteCallback={handleDeleteImage}
          filesSelectCallback={handleSelectImage}
          fileUploadCallback={handleUploadImage}
          onClose={handleCancelLibImg}
        />
      )}

      <div>
        {fileMax === 1 ? (
          <div style={{ marginBottom: 0 }}>
            {fileList.length >= 1 ? (
              <div className={styles.container_library_img}>
                <div className={styles.wrapper_library_img}>
                  <div className={styles.wrapper_library_img_relative}>
                    <img src={fileList[0].url} alt="" className={styles.img} />
                    <div className={styles.matte_layer}>
                      <p style={{ cursor: 'pointer' }} onClick={() => handlePreview(fileList[0])}><DashOutlined /></p>
                      <div className={styles.vertical_lines} />
                      <p style={{ cursor: 'pointer' }} onClick={() => { setIsOpenLibImg(true); }}><PlusOutlined /></p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className={styles.container_library_img} onClick={() => setIsOpenLibImg(true)}>
                <div className={styles.btn_upload_library_img}>
                  {uploadButton}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, maxHeight: 320, overflowY: 'auto' }}>
            {fileList?.map((item, index) => (
              <div className={styles.container_library_img} key={index}>
                <div className={styles.wrapper_library_img}>
                  <img src={item.url} alt="" className={styles.img} />
                </div>
              </div>
            ))}
            {fileList.length < fileMax && (
              <div className={styles.container_library_img} onClick={() => setIsOpenLibImg(true)}>
                <div className={styles.btn_upload_library_img}>
                  {uploadButton}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        open={previewOpen}
        title={previewTitle}
        footer={null}
        onCancel={handleCancel}
      >
        <img
          alt="example"
          style={{
            width: '100%',
          }}
          src={previewImage}
        />
      </Modal>
    </>
  );
}
